import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HoverHint from '../../../components/HoverHint';
import { getCurrentDirectorMapping, startDirectorMapping } from '../../../api';
import type {
  DirectorMappingBlockReason,
  DirectorMappingErrorCode,
  DirectorMappingState,
  DirectorRunView,
  DirectorRunStatus,
  Episode,
} from '../../../types';

const ACTIVE_STATUSES = new Set<DirectorRunStatus>(['queued', 'running']);

const ACTION_KEYS: Record<DirectorMappingState['recommendedAction'], string> = {
  generate: 'directorMapping.generate',
  update: 'directorMapping.update',
  retry: 'directorMapping.retry',
  none: 'directorMapping.retry',
};

const BLOCK_REASON_KEYS: Record<DirectorMappingBlockReason, string> = {
  format_rewrite_required: 'directorMapping.block.formatRewriteRequired',
  director_annotation_required: 'directorMapping.block.annotationRequired',
  storyboard_required: 'directorMapping.block.storyboardRequired',
  forbidden: 'directorMapping.block.forbidden',
  director_mapping_input_too_large: 'directorMapping.block.inputTooLarge',
  insufficient_credits: 'directorMapping.block.insufficientCredits',
  mapping_config_unavailable: 'directorMapping.block.configUnavailable',
};

const ACCOUNTING_RECOVERY_ABANDONED_REASON = 'director mapping accounting recovery abandoned after response checkpoint and audit body remained unavailable';

const TERMINAL_ERROR_KEYS: Record<string, string> = {
  '任务投递失败': 'directorMapping.error.queueUnavailable',
  '任务超重试上限被丢弃': 'directorMapping.error.retryExhausted',
  'director mapping execution lease expired before provider call': 'directorMapping.error.executionExpired',
  [ACCOUNTING_RECOVERY_ABANDONED_REASON]: 'directorMapping.error.accountingRecoveryAbandoned',
  'director mapping provider returned no response; recovered completed accounting partial state': 'directorMapping.error.providerNoResponse',
  'director mapping returned empty content': 'directorMapping.error.invalidResponse',
};

const ERROR_CODE_KEYS: Record<DirectorMappingErrorCode, string> = {
  director_mapping_retry_exhausted: 'directorMapping.error.retryExhausted',
  director_mapping_execution_expired: 'directorMapping.error.executionExpired',
  director_mapping_accounting_recovery_abandoned: 'directorMapping.error.accountingRecoveryAbandoned',
  director_mapping_provider_no_response: 'directorMapping.error.providerNoResponse',
  director_mapping_invalid_response: 'directorMapping.error.invalidResponse',
  mapping_config_unavailable: 'directorMapping.block.configUnavailable',
  director_mapping_execution_failed: 'directorMapping.error.executionFailed',
};

interface DirectorMappingControlsProps {
  episode: Episode;
  refreshToken?: number;
  variant?: 'section' | 'inline';
  onStateChange?: (state: DirectorMappingState) => void;
  onTerminal?: (state: DirectorMappingState) => void;
}

const SECTION_STYLE: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.55rem',
  padding: '0.75rem 0', borderTop: '1px solid var(--border)',
};

const INLINE_STYLE: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: '0.55rem',
};

interface PendingStateLoad {
  notifyTerminal: boolean;
  resolvers: Array<(state: DirectorMappingState | null) => void>;
}

function errorCode(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const value = error as { code?: string; body?: { code?: string }; message?: string };
  return value.code || value.body?.code || value.message || '';
}

export function actionErrorMessage(error: unknown, translate: (key: string) => string): string {
  switch (errorCode(error)) {
    case 'format_rewrite_required':
      return translate(BLOCK_REASON_KEYS.format_rewrite_required);
    case 'director_annotation_required':
      return translate(BLOCK_REASON_KEYS.director_annotation_required);
    case 'storyboard_required':
      return translate(BLOCK_REASON_KEYS.storyboard_required);
    case 'forbidden':
      return translate(BLOCK_REASON_KEYS.forbidden);
    case 'director_mapping_queue_unavailable':
      return translate('directorMapping.error.queueUnavailable');
    case 'director_mapping_input_changed':
      return translate('directorMapping.error.inputChanged');
    case 'director_mapping_execution_failed':
      return translate('directorMapping.error.executionFailed');
    case 'director_mapping_input_too_large':
      return translate(BLOCK_REASON_KEYS.director_mapping_input_too_large);
    case 'insufficient_credits':
      return translate(BLOCK_REASON_KEYS.insufficient_credits);
    case 'mapping_config_unavailable':
      return translate(BLOCK_REASON_KEYS.mapping_config_unavailable);
    default:
      return error instanceof Error && error.message ? error.message : translate('directorMapping.error.triggerFailed');
  }
}

function terminalErrorMessage(run: DirectorRunView, translate: (key: string) => string): string {
  if (run.errorCode) return translate(ERROR_CODE_KEYS[run.errorCode]);
  const message = run.errorMsg || '';
  const exactKey = TERMINAL_ERROR_KEYS[message];
  if (exactKey) return translate(exactKey);
  if (message.endsWith(`\n${ACCOUNTING_RECOVERY_ABANDONED_REASON}`)) {
    return translate('directorMapping.error.accountingRecoveryAbandoned');
  }
  if (message.startsWith('director mapping configuration:')) {
    return translate(BLOCK_REASON_KEYS.mapping_config_unavailable);
  }
  if (message.startsWith('invalid director mapping output')) {
    return translate('directorMapping.error.invalidResponse');
  }
  return message;
}

export default function DirectorMappingControls({
  episode,
  refreshToken = 0,
  variant = 'section',
  onStateChange,
  onTerminal,
}: DirectorMappingControlsProps) {
  const containerStyle = variant === 'inline' ? INLINE_STYLE : SECTION_STYLE;
  const { t } = useTranslation('episodeDetail');
  const [mappingState, setMappingState] = useState<DirectorMappingState | null>(null);
  const [mappingEpisodeId, setMappingEpisodeId] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [actionError, setActionError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const mountedRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const stateEpochRef = useRef(0);
  const episodeIdRef = useRef(episode.id);
  const coordinatedEpisodeIdRef = useRef('');
  const initialTerminalNotificationOwedRef = useRef('');
  const stateRequestInFlightRef = useRef(false);
  const pendingStateLoadRef = useRef<PendingStateLoad | null>(null);
  const pollTimerRef = useRef<number | undefined>(undefined);
  const acceptedActiveRef = useRef(false);
  const onStateChangeRef = useRef(onStateChange);
  const onTerminalRef = useRef(onTerminal);
  const runStateRequestRef = useRef<(
    notifyTerminal: boolean,
    resolvers: PendingStateLoad['resolvers'],
  ) => void>(() => undefined);
  const drainStateLoadsRef = useRef<() => void>(() => undefined);
  const schedulePollRef = useRef<() => void>(() => undefined);

  episodeIdRef.current = episode.id;

  useEffect(() => { onStateChangeRef.current = onStateChange; }, [onStateChange]);
  useEffect(() => { onTerminalRef.current = onTerminal; }, [onTerminal]);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current !== undefined) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = undefined;
    }
  }, []);

  const schedulePoll = useCallback(() => {
    if (
      !mountedRef.current
      || !acceptedActiveRef.current
      || stateRequestInFlightRef.current
      || pendingStateLoadRef.current
      || pollTimerRef.current !== undefined
    ) return;

    pollTimerRef.current = window.setTimeout(() => {
      pollTimerRef.current = undefined;
      if (
        !mountedRef.current
        || !acceptedActiveRef.current
        || stateRequestInFlightRef.current
        || pendingStateLoadRef.current
      ) return;
      runStateRequestRef.current(true, []);
    }, 2000);
  }, []);

  const drainStateLoads = useCallback(() => {
    if (!mountedRef.current || stateRequestInFlightRef.current) return;
    const pending = pendingStateLoadRef.current;
    if (!pending) {
      schedulePollRef.current();
      return;
    }
    pendingStateLoadRef.current = null;
    runStateRequestRef.current(pending.notifyTerminal, pending.resolvers);
  }, []);

  const runStateRequest = useCallback((
    notifyTerminal: boolean,
    resolvers: PendingStateLoad['resolvers'],
  ) => {
    if (!mountedRef.current || stateRequestInFlightRef.current) {
      resolvers.forEach(resolve => resolve(null));
      return;
    }

    stateRequestInFlightRef.current = true;
    const generation = requestGenerationRef.current;
    const stateEpoch = stateEpochRef.current;
    const requestEpisodeId = episodeIdRef.current;
    const request = Promise.resolve().then(() => getCurrentDirectorMapping(requestEpisodeId));

    void request.then(next => {
      if (
        !mountedRef.current
        || generation !== requestGenerationRef.current
        || stateEpoch !== stateEpochRef.current
        || requestEpisodeId !== episodeIdRef.current
      ) return null;

      const nextStatus = next.latestAttempt?.status;
      const nextActive = Boolean(nextStatus && ACTIVE_STATUSES.has(nextStatus));
      if (initialTerminalNotificationOwedRef.current === requestEpisodeId) {
        initialTerminalNotificationOwedRef.current = '';
      }
      acceptedActiveRef.current = nextActive;
      if (!nextActive) clearPollTimer();
      setLoadFailed(false);
      setMappingState(next);
      setMappingEpisodeId(requestEpisodeId);
      onStateChangeRef.current?.(next);
      if (notifyTerminal && next.latestAttempt && !nextActive) {
        onTerminalRef.current?.(next);
      }
      return next;
    }, () => {
      if (
        mountedRef.current
        && generation === requestGenerationRef.current
        && stateEpoch === stateEpochRef.current
        && requestEpisodeId === episodeIdRef.current
      ) {
        setLoadFailed(true);
      }
      return null;
    }).catch(() => null).then(next => {
      stateRequestInFlightRef.current = false;
      if (mountedRef.current) {
        if (pendingStateLoadRef.current) drainStateLoadsRef.current();
        else schedulePollRef.current();
      }
      resolvers.forEach(resolve => resolve(next));
    });
  }, [clearPollTimer]);

  schedulePollRef.current = schedulePoll;
  drainStateLoadsRef.current = drainStateLoads;
  runStateRequestRef.current = runStateRequest;

  const requestStateImmediately = useCallback((notifyTerminal = false) => {
    clearPollTimer();
    if (!mountedRef.current) return Promise.resolve(null);

    return new Promise<DirectorMappingState | null>(resolve => {
      const pending = pendingStateLoadRef.current;
      if (pending) {
        pending.notifyTerminal = pending.notifyTerminal || notifyTerminal;
        pending.resolvers.push(resolve);
      } else {
        pendingStateLoadRef.current = { notifyTerminal, resolvers: [resolve] };
      }
      drainStateLoadsRef.current();
    });
  }, [clearPollTimer]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestGenerationRef.current += 1;
      stateEpochRef.current += 1;
      acceptedActiveRef.current = false;
      clearPollTimer();
      const pending = pendingStateLoadRef.current;
      pendingStateLoadRef.current = null;
      pending?.resolvers.forEach(resolve => resolve(null));
    };
  }, [clearPollTimer]);

  useEffect(() => {
    if (coordinatedEpisodeIdRef.current !== episode.id) {
      coordinatedEpisodeIdRef.current = episode.id;
      initialTerminalNotificationOwedRef.current = episode.id;
      requestGenerationRef.current += 1;
      stateEpochRef.current += 1;
      acceptedActiveRef.current = false;
    }
    setActionError('');
    setLoadFailed(false);
    void requestStateImmediately(initialTerminalNotificationOwedRef.current === episode.id);
  }, [episode.id, refreshToken, requestStateImmediately]);

  const visibleState = mappingEpisodeId === episode.id ? mappingState : null;
  const latestStatus = visibleState?.latestAttempt?.status;
  const active = latestStatus ? ACTIVE_STATUSES.has(latestStatus) : false;

  const handleTrigger = async () => {
    if (!visibleState?.canTrigger || active || triggering) return;
    const triggerGeneration = requestGenerationRef.current;
    const triggerEpisodeId = episode.id;
    const triggerEpoch = stateEpochRef.current + 1;
    stateEpochRef.current = triggerEpoch;
    setTriggering(true);
    setActionError('');
    try {
      const response = await startDirectorMapping(episode.id);
      if (
        !mountedRef.current
        || triggerGeneration !== requestGenerationRef.current
        || triggerEpisodeId !== episodeIdRef.current
        || triggerEpoch !== stateEpochRef.current
      ) return;
      if (ACTIVE_STATUSES.has(response.status)) {
        acceptedActiveRef.current = true;
        setMappingState(previous => {
          if (!previous) return previous;
          const next: DirectorMappingState = {
            ...previous,
            latestAttempt: response,
            recommendedAction: 'none',
          };
          onStateChangeRef.current?.(next);
          return next;
        });
        schedulePollRef.current();
      } else {
        await requestStateImmediately(true);
      }
    } catch (error) {
      if (
        !mountedRef.current
        || triggerGeneration !== requestGenerationRef.current
        || triggerEpisodeId !== episodeIdRef.current
        || triggerEpoch !== stateEpochRef.current
      ) return;
      setActionError(actionErrorMessage(error, t));
      await requestStateImmediately();
    } finally {
      if (mountedRef.current) setTriggering(false);
    }
  };

  if (!visibleState) {
    if (!loadFailed) {
      return <div aria-label={t('directorMapping.ariaLabel')} style={{ color: 'var(--text-3)', fontSize: '0.85rem' }}>{t('directorMapping.loading')}</div>;
    }
    return (
      <section aria-label={t('directorMapping.ariaLabel')} style={containerStyle}>
        <a
          href={`/projects/${encodeURIComponent(episode.projectId)}/episodes/${encodeURIComponent(episode.id)}/director-annotations`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-secondary"
          style={{ fontSize: '0.82rem' }}
        >
          {t('directorMapping.annotations')}
        </a>
        <button type="button" className="btn btn-secondary" onClick={() => { setLoadFailed(false); void requestStateImmediately(); }} style={{ fontSize: '0.82rem' }}>
          {t('directorMapping.reload')}
        </button>
        <span role="alert" style={{ color: 'var(--danger)', fontSize: '0.82rem' }}>{t('directorMapping.error.loadFailed')}</span>
      </section>
    );
  }

  const busy = active || triggering;
  const buttonLabel = busy ? t('directorMapping.running') : t(ACTION_KEYS[visibleState.recommendedAction]);
  const blocker = visibleState.blockReason ? t(BLOCK_REASON_KEYS[visibleState.blockReason]) : '';

  return (
    <section
      aria-label={t('directorMapping.ariaLabel')}
      style={containerStyle}
    >
      <a
        href={`/projects/${encodeURIComponent(episode.projectId)}/episodes/${encodeURIComponent(episode.id)}/director-annotations`}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-secondary"
        style={{ fontSize: '0.82rem' }}
      >
        {t('directorMapping.annotations')}
      </a>
      {/* 禁用原因的即时 hover 提示：气泡 portal 到 body 防容器裁剪 */}
      <HoverHint
        hint={blocker || undefined}
        cursor={(!visibleState.canTrigger || busy) ? 'not-allowed' : undefined}
      >
        <button
          type="button"
          className="btn btn-secondary"
          disabled={!visibleState.canTrigger || busy}
          aria-busy={busy ? 'true' : undefined}
          onClick={handleTrigger}
          style={{ fontSize: '0.82rem', pointerEvents: (!visibleState.canTrigger || busy) ? 'none' : undefined }}
        >
          {busy && <span className="loading-spinner" />}
          {buttonLabel}
        </button>
      </HoverHint>
      {visibleState.currentResult && <span style={{ color: 'var(--success)', fontSize: '0.82rem' }}>{t('directorMapping.completed')}</span>}
      {visibleState.needsUpdate && !visibleState.currentResult && <span style={{ color: 'var(--warning)', fontSize: '0.82rem' }}>{t('directorMapping.needsUpdate')}</span>}
      {visibleState.latestAttempt?.status === 'failed'
        && (visibleState.latestAttempt.errorCode || visibleState.latestAttempt.errorMsg) && (
        <span role="alert" style={{ color: 'var(--danger)', fontSize: '0.82rem' }}>
          {terminalErrorMessage(visibleState.latestAttempt, t)}
        </span>
      )}
      {actionError && <span role="alert" style={{ color: 'var(--danger)', fontSize: '0.82rem' }}>{actionError}</span>}
      {loadFailed && <span role="alert" style={{ color: 'var(--danger)', fontSize: '0.82rem' }}>{t('directorMapping.error.loadFailed')}</span>}
    </section>
  );
}
